import simplify from './simplify';
import ReconnectingWebSocket from './websocket';
import Elements from './elements';
import { prompt, alert } from './alert';

const wsURI = (password) => {
  const l = window.location;
  const uri = `${l.protocol === 'https:' ? 'wss:' : 'ws:'}//${l.host}${l.pathname}/ws`;
  if (password === undefined) {
    return uri;
  }
  return `${uri}?access_token=${encodeURIComponent(password)}`;
};

export default class Model {
  constructor() {
    this.canDraw = false;
    this.elements = new Elements();
    this.past = [];
    this.future = [];
    this.listeners = [];
    this.boardName = window.location.pathname.substring(1);
    this.unauthorized = false;

    this.pending = new Map(JSON.parse(localStorage[this.boardName] || '[]'));
    this.pending.forEach((cmd) => {
      if (cmd.element) {
        this.elements.put(cmd.id, cmd.element);
      } else if (cmd.remove) {
        this.elements.rm(cmd.remove.id);
      }
    });
    this.connectWebSocket();
  }

  connectWebSocket() {
    this.ws = new ReconnectingWebSocket(wsURI(''));
    this.ws.onopen = () => {
      this.pending.forEach((value) => {
        this.ws.send(JSON.stringify(value));
      });
      this.online = true;
      this.notify();
    };
    this.ws.onclose = () => { this.online = false; this.notify(); };
    this.ws.onmessage = (e) => {
      const m = JSON.parse(e.data);
      this.unauthorized = false;
      if (m.init) {
        this.elements.map(id => this.elements.rm(id));
        Object.keys(m.init.elements||[]).forEach(id => this.elements.put(id, m.init.elements[id]));
        this.pending.forEach((cmd) => {
          if (cmd.remove) {
            this.elements.rm(cmd.remove.id);
          }
        });
        this.notify();
      } else if (m.ack) {
        this.pending.delete(m.ack.id);
        localStorage[this.boardName] = JSON.stringify(Array.from(this.pending.entries()));
        if (m.ack.code === 401) {
          this.unauthorized = true;
          this.ws.close();
          this.notify();
          prompt({
            title: 'Login',
            content: 'This board is password protected:',
            password: true,
            forced: true,
          }).then(password => this.openBoardWithPassword(password), () => {});
        } else if (m.ack.code) {
          alert({
            title: 'Sorry',
            content: `Something went wrong: ${m.ack.message}`,
          });
        }
      } else if (m.command) {
        const cmd = m.command;
        if (cmd.element) {
          this.elements.put(cmd.id, cmd.element);
        } else if (cmd.remove) {
          this.elements.rm(cmd.remove.id);
        } else if (cmd.rename) {
          window.location.pathname = `/${cmd.rename.name}`;
        }
        this.notify();
      }
    };
  }

  publish(id, cmd, data, retry) {
    if (retry) {
      this.pending.set(id, { id, [cmd]: data });
      localStorage[this.boardName] = JSON.stringify(Array.from(this.pending.entries()));
    }
    if (this.online) {
      this.ws.send(JSON.stringify({ id, [cmd]: data }));
    }
  }

  subscribe(cb) {
    this.listeners.push(cb);
  }

  unsubscribe(cb) {
    this.listeners = this.listeners.filter(ln => ln !== cb);
  }

  notify() {
    this.listeners.forEach(ln => ln());
  }

  pathStart({ x, y }) {
    const id = this.elements.id();
    const el = { stroke: { points: [{ x, y }] } };
    this.elements.put(id, el);
    this.publish(id, 'element', el, true);
    this.notify();
    return id;
  }

  pathAdd({ id, x, y }) {
    const el = this.elements.get(id);
    el.stroke.points.push({ x, y });
    this.elements.put(id, el);
    this.publish(id, 'element', el, true);
    this.notify();
  }

  pathEnd(id) {
    const el = this.elements.get(id);
    el.stroke.points = simplify(el.stroke.points, 2);
    this.addHistory('add', id, el);
    this.elements.put(id, el);
    this.publish(id, 'element', el, true);
    this.notify();
  }

  textCreate({ x, y, text }) {
    const id = this.elements.id();
    const el = { text: { position: { x, y }, text } };
    this.addHistory('add', id, el);
    this.elements.put(id, el);
    this.publish(id, 'element', el, true);
    this.notify();
    return id;
  }

  textUpdate(id, text) {
    const el = this.elements.get(id);
    el.text.text = text;
    this.addHistory('add', id, el);
    this.elements.put(id, el);
    this.publish(id, 'element', el, true);
    this.notify();
  }

  remove(id, silent) {
    this.addHistory('remove', id, this.elements.get(id));
    this.elements.rm(id);
    if (!silent) {
      this.publish(this.elements.id(), 'remove', { id }, true);
    }
    this.notify();
  }

  addHistory(action, id, el) {
    if (!el) {
      return;
    }
    if (this.past.length === 0 || this.past[this.past.length - 1].id !== id) {
      this.past.push({ action, id, el });
      this.future = [];
    }
  }

  undo() {
    const a = this.past.pop();
    if (!a) {
      return;
    }
    if (a.action === 'add') {
      this.elements.rm(a.id);
      this.publish(this.elements.id(), 'remove', { id: a.id }, true);
    } else if (a.action === 'remove') {
      this.elements.put(a.id, a.el);
      this.publish(a.id, 'element', a.el, true);
    }
    this.future.push(a);
    this.notify();
  }

  redo() {
    const a = this.future.pop();
    if (!a) {
      return;
    }
    if (a.action === 'add') {
      this.elements.put(a.id, a.el);
      this.publish(a.id, 'element', a.el, true);
    } else if (a.action === 'remove') {
      this.elements.rm(a.id);
      this.publish(this.elements.id(), 'remove', { id: a.id }, true);
    }
    this.past.push(a);
    this.notify();
  }

  renameBoard(name) {
    this.publish(this.elements.id(), 'rename', { name }, false);
  }

  protectBoard(password) {
    this.ws.url = wsURI(password);
    this.publish(this.elements.id(), 'password', { password }, false);
  }

  openBoardWithPassword(password) {
    this.ws.url = wsURI(password);
    this.ws.refresh();
    this.ws.close();
    this.ws.open();
  }
}
